import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

interface WindowPortalProps {
  children: React.ReactNode;
  title?: string;
  width?: number;
  height?: number;
}

const WindowPortal: React.FC<WindowPortalProps> = ({
  children,
  title = "New Window",
  width = 800,
  height = 800,
}) => {
  const externalWindow = useRef<Window | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const container = document.createElement("div");
    containerRef.current = container;

    const left = window.screenX + 100;
    const top = window.screenY + 100;
    const features = `width=${width},height=${height},left=${left},top=${top},resizable,scrollbars`;

    const newWindow = window.open("", "", features);
    externalWindow.current = newWindow;

    if (!newWindow || !containerRef.current) return;

    newWindow.document.title = title;
    newWindow.document.body.style.margin = "0";
    newWindow.document.body.appendChild(containerRef.current);

    // Suppress ResizeObserver console spam
    const winWithConsole = newWindow as any;
    const originalConsoleError = winWithConsole.console?.error;
    if (originalConsoleError) {
      winWithConsole.console.error = function (...args: any[]) {
        if (
          args[0]?.toString().includes("ResizeObserver loop limit exceeded") ||
          args[0]?.toString().includes(
            "ResizeObserver loop completed with undelivered notifications"
          )
        ) {
          return;
        }
        originalConsoleError.apply(winWithConsole.console, args);
      };
    }

    // Style injection
    Array.from(document.styleSheets).forEach((styleSheet) => {
      try {
        if (styleSheet.href) {
          const linkEl = newWindow.document.createElement("link");
          linkEl.rel = "stylesheet";
          linkEl.href = styleSheet.href;
          newWindow.document.head.appendChild(linkEl);
        } else if (styleSheet.cssRules) {
          const styleEl = newWindow.document.createElement("style");
          Array.from(styleSheet.cssRules).forEach((rule) => {
            styleEl.appendChild(document.createTextNode(rule.cssText));
          });
          newWindow.document.head.appendChild(styleEl);
        }
      } catch (err) {
        console.warn("Could not copy style:", err);
      }
    });

    const cleanup = () => {
      if (containerRef.current && containerRef.current.parentNode) {
        containerRef.current.parentNode.removeChild(containerRef.current);
      }
      containerRef.current = null;
      newWindow.close();
    };

    setMounted(true);
    newWindow.addEventListener("beforeunload", cleanup);

    return () => {
      newWindow.removeEventListener("beforeunload", cleanup);
      cleanup();
    };
  }, [title, width, height]);

  return mounted && containerRef.current
    ? ReactDOM.createPortal(children, containerRef.current)
    : null;
};

export default WindowPortal;

